import { useCallback, useEffect, useMemo, useState } from 'react';
import { dashboardPrefs } from '@/api/endpoints';
import type { DashboardLayoutItem } from '@/types';
import { DEFAULT_LAYOUT, WIDGETS, WIDGET_MAP } from './registry';

/**
 * Ładuje układ dashboardu z serwera i uzgadnia go z rejestrem:
 *  - odrzuca klucze widgetów spoza rejestru (usunięte),
 *  - dołącza na końcu (widoczne) widgety z rejestru, których użytkownik jeszcze nie ma
 *    (nowe widgety pojawiają się automatycznie),
 *  - koryguje niedozwolone szerokości do domyślnej.
 * Brak zapisu → układ domyślny.
 */
function reconcile(saved: DashboardLayoutItem[] | null | undefined): DashboardLayoutItem[] {
  if (!saved || saved.length === 0) return DEFAULT_LAYOUT.map(x => ({ ...x }));
  const known: DashboardLayoutItem[] = saved
    .filter(it => it && WIDGET_MAP[it.key])
    .map(it => {
      const def = WIDGET_MAP[it.key];
      const width = def.widths.includes(it.width) ? it.width : def.defaultWidth;
      return { key: it.key, visible: it.visible !== false, width };
    });
  const seen = new Set(known.map(it => it.key));
  const appended: DashboardLayoutItem[] = WIDGETS
    .filter(w => !seen.has(w.key))
    .map(w => ({ key: w.key, visible: true, width: w.defaultWidth }));
  return [...known, ...appended];
}

export function useDashboardLayout() {
  const [layout, setLayout] = useState<DashboardLayoutItem[]>(() => DEFAULT_LAYOUT.map(x => ({ ...x })));
  const [savedLayout, setSavedLayout] = useState<DashboardLayoutItem[]>(() => DEFAULT_LAYOUT.map(x => ({ ...x })));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    dashboardPrefs.get()
      .then(res => {
        if (cancelled) return;
        const reconciled = reconcile(res?.layout ?? null);
        setLayout(reconciled);
        setSavedLayout(reconciled);
      })
      .catch(() => { /* brak/niedostępne → zostaje domyślny */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const dirty = useMemo(
    () => JSON.stringify(layout) !== JSON.stringify(savedLayout),
    [layout, savedLayout],
  );

  const save = useCallback(async () => {
    setSaving(true);
    try {
      await dashboardPrefs.put(layout);
      setSavedLayout(layout.map(x => ({ ...x })));
    } finally {
      setSaving(false);
    }
  }, [layout]);

  /** Odrzuć niezapisane zmiany (Anuluj). */
  const revert = useCallback(() => setLayout(savedLayout.map(x => ({ ...x }))), [savedLayout]);

  /** Ustaw układ domyślny (stage — utrwalany dopiero przez „Zapisz"). */
  const resetDefault = useCallback(() => setLayout(DEFAULT_LAYOUT.map(x => ({ ...x }))), []);

  return { layout, setLayout, save, revert, resetDefault, dirty, loading, saving };
}
